/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef } from 'react'

import { addEventListenerPassive, elementPosition } from '@/core/utils/dom/utils'

import useDebounce from '../useDebounce'

interface ActiveOnScrollProps {
  childNodes: NodeListOf<ChildNode>
  viewPercentage?: number
}

const getActiveOnHorizontalScroll = ({ childNodes, viewPercentage = 40 }: ActiveOnScrollProps) => {
  const initialArea = (window.innerWidth * viewPercentage) / 100

  for (const index in [...childNodes]) {
    const element = childNodes[index] as HTMLDivElement
    const position = elementPosition(element)
    const positionEnd = position.x + element.offsetWidth

    const inLimite = positionEnd > initialArea

    if (inLimite) {
      return +index
    }
  }

  return 0
}

type TActivatedItemScrollHook<Ref> = {
  onActive: (index: number) => void
  active: number
  viewPercentage?: number
  ref?: Ref
  debounceDelay?: number
  enabled?: boolean
}

export const useScrollActivatedItem = <Ref extends React.MutableRefObject<any>>({
  onActive,
  ref,
  viewPercentage = 40,
  debounceDelay = 50,
  enabled = true,
}: TActivatedItemScrollHook<Ref>) => {
  const newRef = useRef(null) as Ref

  const onScroll = useCallback(
    (event: Event): void => {
      if (event.target) {
        const childNodes = (event.target as HTMLDivElement).childNodes
        const activeIdx = getActiveOnHorizontalScroll({ childNodes, viewPercentage })
        onActive(activeIdx)
      }
    },
    [onActive, viewPercentage],
  )

  const debounceScroll = useDebounce(onScroll, debounceDelay)

  useEffect(() => {
    const scrollRef = ref || newRef

    if (enabled) {
      addEventListenerPassive('scroll', debounceScroll, scrollRef.current)
    }

    return () => scrollRef.current?.removeEventListener('scroll', debounceScroll)
  }, [debounceScroll, ref, enabled])

  return {
    ref: ref || newRef,
  }
}
